import { useCallback, useEffect, useMemo, useState } from "react";
import { api, mesajEroare } from "./api.js";
import {
  DESTINATII_DOSAR, FRECVENTE, OBIECTE_OBLIGATIE, STARI_CONFORMITATE,
  SURSE_OBLIGATIE, eticheta,
} from "./enums.js";
import { data, dataOra } from "./text.js";
import { tr } from "./i18n.js";

const SCRIE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR"];
const ADMINISTREAZA = ["ADMIN_TENANT", "MANAGER_TEHNIC"];

/**
 * Cum se citește un verdict, și de ce „nedeterminat" nu e un eșec al aplicației.
 *
 * „Nedeterminat" înseamnă că dovada cerută nu se poate căuta automat — un
 * registru ținut pe hârtie la obiectiv, de pildă. E ea însăși o constatare, pe
 * care dosarul o SCRIE, nu o ascunde. Un registru care tace acolo unde nu știe
 * e mai periculos decât unul care spune „aici trebuie să vă uitați voi".
 */
const CULOARE_STARE = {
  Conform: "eticheta eticheta-activ",
  Neconform: "eticheta eticheta-inactiv",
  ScadentCurand: "eticheta marcaj-atentie",
  Nedeterminat: "eticheta",
};

/** Cât mai e până la limita cu tot cu toleranță — și cât de tare sare în ochi. */
function termen(zile) {
  if (zile < 0) return { text: tr("depășit cu {0} {1}", -zile, -zile === 1 ? "zi" : "zile"), clasa: "scadent-depasit" };
  if (zile === 0) return { text: tr("expiră azi"), clasa: "scadent-azi" };
  if (zile <= 30) return { text: `${zile} ${zile === 1 ? "zi" : "zile"}`, clasa: "scadent-aproape" };
  return { text: tr("{0} de zile", zile), clasa: "" };
}

/** Unde să se uite omul când o obligație n-are pe ce se aplica. */
const OBIECTE_LIPSA = {
  ClientObiectiv: "Clientul n-are nicio locație activă.",
  SistemInstalat: "Clientul n-are niciun sistem instalat activ.",
  Tehnician: "Firma n-are niciun tehnician activ.",
  FirmaPrestatoare: "",
};

/**
 * O obligație copiată dintr-un șablon și neatinsă de atunci nu are sursa
 * ALEASĂ de nimeni: `copiazaSablon` pune „politică internă" doar fiindcă
 * schema cere o valoare. A o TIPĂRI ca atare e o afirmație pe care firma n-a
 * făcut-o — iar „contract de mentenanță" trecut drept politică internă în
 * dosarul unui control e chiar felul de neadevăr pe care modulul îl previne.
 * Aceeași onestitate ca la temei: se spune că lipsește, nu se ghicește.
 */
function sursaAleasa(o) {
  return o.sablonId && o.versiune === 1 && !o.temeiLegal;
}

export default function Conformitate({ eu }) {
  const [clienti, setClienti] = useState([]);
  const [clientId, setClientId] = useState("");
  const [sub, setSub] = useState("registru");
  const [eroare, setEroare] = useState("");

  const poateScrie = SCRIE.includes(eu.rol);
  const poateAdministra = ADMINISTREAZA.includes(eu.rol);

  useEffect(() => {
    api.clienti().then((l) => {
      setClienti(l);
      if (l.length === 1) setClientId(l[0].id);
    }).catch((e) => setEroare(mesajEroare(e)));
  }, []);

  return (
    <div className="continut">
      <h1>{tr("Registrul de conformitate")}</h1>
      <p className="subtitlu">
        {tr("Ce trebuie ținut la zi la fiecare client, când e scadent, și ce dovadă are aplicația că s-a făcut. Temeiul fiecărei obligații îl scrie firma și și-l asumă cu numele ei — noi livrăm doar lista de subiecte de acoperit, niciodată textul juridic.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou">
        <div className="panou-corp">
          <label className="camp">{tr("Client")}
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">{tr("— alege clientul —")}</option>
              {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
            </select>
          </label>
        </div>
      </div>

      {!clientId ? (
        <div className="gol">
          {tr("Registrul se ține pe client: obligațiile unui obiectiv depind de ce e instalat acolo și de ce s-a semnat în contract. Alege un client.")}
        </div>
      ) : (
        <>
          <div className="taburi-sisteme">
            <button className={`tab-sistem ${sub === "registru" ? "activ" : ""}`}
              onClick={() => setSub("registru")}>{tr("Registru")}</button>
            <button className={`tab-sistem ${sub === "obligatii" ? "activ" : ""}`}
              onClick={() => setSub("obligatii")}>{tr("Obligații")}</button>
            <button className={`tab-sistem ${sub === "dosare" ? "activ" : ""}`}
              onClick={() => setSub("dosare")}>{tr("Dosare")}</button>
          </div>

          {sub === "registru" && <Registru clientId={clientId} poateScrie={poateScrie} />}
          {sub === "obligatii" && <Obligatii clientId={clientId} poateScrie={poateScrie} />}
          {sub === "dosare" && (
            <Dosare clientId={clientId} poateGenera={poateAdministra} />
          )}
        </>
      )}
    </div>
  );
}

// ── Registrul: verdictul, calculat la citire ────────────────────────────────

function Registru({ clientId, poateScrie }) {
  const [randuri, setRanduri] = useState(null);
  const [eroare, setEroare] = useState("");
  const [doar, setDoar] = useState("");

  const reincarca = useCallback(async () => {
    setRanduri(null);
    try { setRanduri(await api.registruConformitate(clientId)); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); setRanduri([]); }
  }, [clientId]);

  useEffect(() => { reincarca(); }, [reincarca]);

  const vizibile = useMemo(
    () => (randuri ?? []).filter((r) => !doar || r.status === doar),
    [randuri, doar],
  );

  const numarate = useMemo(() => {
    const n = { Conform: 0, ScadentCurand: 0, Neconform: 0, Nedeterminat: 0 };
    for (const r of randuri ?? []) n[r.status] = (n[r.status] ?? 0) + 1;
    return n;
  }, [randuri]);

  async function marcheaza(r) {
    try { await api.verificaInstanta(r.id, {}); await reincarca(); }
    catch (e) { setEroare(mesajEroare(e)); }
  }

  if (randuri === null) return <div className="gol">{tr("Se încarcă…")}</div>;

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}

      <div className="cifre">
        {Object.entries(STARI_CONFORMITATE).map(([cod, et]) => (
          <div className="cifra" key={cod}>
            <div className="cifra-valoare">{numarate[cod] ?? 0}</div>
            <div className="cifra-eticheta">{tr(et)}</div>
          </div>
        ))}
      </div>

      <div className="panou">
        <div className="panou-corp">
          <label className="camp">{tr("Arată")}
            <select value={doar} onChange={(e) => setDoar(e.target.value)}>
              <option value="">{tr("toate scadențele")}</option>
              {Object.entries(STARI_CONFORMITATE).map(([v, et]) => (
                <option key={v} value={v}>{tr("doar")} {tr(et)}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {randuri.length === 0 ? (
        <div className="gol">
          {tr("Nicio scadență. Obligațiile se scriu în tabul „Obligații\", iar scadențele se nasc din ele — pe fiecare sistem, tehnician sau obiectiv la care se aplică.")}
        </div>
      ) : (
        <div className="panou tabel-derulabil">
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Obligație")}</th>
                <th>{tr("Se aplică la")}</th>
                <th>{tr("Scadență")}</th>
                <th>{tr("Termen")}</th>
                <th>{tr("Stare")}</th>
                <th>{tr("Dovada")}</th>
                {poateScrie && <th>{tr("Acțiuni")}</th>}
              </tr>
            </thead>
            <tbody>
              {vizibile.map((r) => {
                const t = termen(r.zile);
                return (
                  <tr key={r.id} className={r.status === "Neconform" ? "rand-neconform" : ""}>
                    <td>
                      {tr(r.obligatie.denumire)}
                      {/* „.cod-mic" are margin-left: e gândită să URMEZE textul. */}
                      <span className="cod-mic">{tr("v")}{r.obligatie.versiune}</span>
                      <div className="rezultat-context">
                        {r.obligatie.temeiLegal
                          ? `${eticheta(SURSE_OBLIGATIE, r.obligatie.sursa)} · ${r.obligatie.temeiLegal}`
                          : tr("sursă și temei necompletate")}
                      </div>
                      {r.obligatie.asumatDe && (
                        <div className="rezultat-context">
                          {tr("asumat de")} {r.obligatie.asumatDe}, {data(r.obligatie.asumatLa)}
                        </div>
                      )}
                    </td>
                    <td>{r.obiectNume}</td>
                    <td><span className="scadenta">{data(r.scadenta)}</span></td>
                    <td className={t.clasa}>{t.text}</td>
                    <td><span className={CULOARE_STARE[r.status]}>
                      {eticheta(STARI_CONFORMITATE, r.status)}
                    </span></td>
                    <td>
                      {r.dovezi.length > 0 ? (
                        <>
                          {/* Pe ecran ajung primele trei: un client cu un an de
                              lucru în spate are zeci de fișe care probează
                              aceeași obligație, iar rândul devine mai înalt
                              decât ecranul. Dosarul le poartă pe toate — acolo
                              contează fiecare, aici contează că EXISTĂ. */}
                          {r.dovezi.slice(0, 3).map((d) => (
                            <div key={`${d.fel}-${d.id}`} className="rezultat-context">
                              {tr(d.descriere)} <span className="cod-mic">{data(d.la)}</span>
                            </div>
                          ))}
                          {r.dovezi.length > 3 && (
                            <div className="rezultat-context">
                              {tr("și încă")} {r.dovezi.length - 3} {tr("— toate intră în dosar")}
                            </div>
                          )}
                        </>
                      ) : r.neprobabile.length > 0 ? (
                        /* Ce nu se poate căuta automat se SPUNE, nu se tace. */
                        <div className="rezultat-context">
                          {tr("nu se poate proba automat:")} {r.neprobabile.join(", ")} {tr("— se verifică la fața locului")}
                        </div>
                      ) : (
                        <div className="rezultat-context">{tr("nicio dovadă în perioadă")}</div>
                      )}
                      {r.ultimaVerificare && (
                        <div className="rezultat-context">
                          {tr("verificat de")} {r.verificatDe ?? "—"}, {dataOra(r.ultimaVerificare)}
                        </div>
                      )}
                    </td>
                    {poateScrie && (
                      <td className="celula-actiuni">
                        <button className="buton-mic" onClick={() => marcheaza(r)}>
                          {tr("am verificat")}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Obligațiile: ce ține firma la zi, și pe ce temei ────────────────────────

function Obligatii({ clientId, poateScrie }) {
  const [lista, setLista] = useState(null);
  const [sabloane, setSabloane] = useState([]);
  const [eroare, setEroare] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [editata, setEditata] = useState(null);
  const [istoric, setIstoric] = useState(null);
  const [noua, setNoua] = useState(false);

  const reincarca = useCallback(async () => {
    setLista(null);
    try { setLista(await api.obligatii(clientId)); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); setLista([]); }
  }, [clientId]);

  useEffect(() => { reincarca(); }, [reincarca]);
  useEffect(() => { api.sabloaneObligatii().then(setSabloane).catch(() => {}); }, []);

  const luate = useMemo(
    () => new Set((lista ?? []).map((o) => o.sablonId).filter(Boolean)),
    [lista],
  );

  async function copiaza(sablonId) {
    try { await api.copiazaObligatie(clientId, sablonId); await reincarca(); }
    catch (e) { setEroare(mesajEroare(e)); }
  }

  async function genereaza(o) {
    try {
      const r = await api.genereazaInstante(o.id);
      setEroare("");
      await reincarca();
      // Rezultatul se scrie pe ecran, nu într-un `alert`: un dialog de sistem
      // se închide și nu mai rămâne nimic din el, iar cifra asta e chiar
      // răspunsul la „a făcut ceva butonul?".
      // Zero obiecte și zero scadențe noi sunt două lucruri diferite: primul
      // înseamnă „n-are pe ce se aplica" (un client fără locații, o firmă fără
      // tehnicieni activi), al doilea „le are deja". Un singur mesaj pentru
      // amândouă l-ar trimite pe om să caute unde nu e.
      setMesaj(r.obiecte === 0
        ? tr("„{0}\": nimic pe care să se aplice. {1}", o.denumire, OBIECTE_LIPSA[o.seAplicaLa] ?? "")
        : r.instante === 0
          ? tr("„{0}\": nicio scadență nouă — {1} deja pe următoarele.", o.denumire, r.obiecte === 1 ? "obiectul vizat o are" : `cele ${r.obiecte} obiecte vizate le au`)
          : `„${o.denumire}": ${r.instante} ${r.instante === 1 ? "scadență" : "scadențe"} pe ${r.obiecte} ${r.obiecte === 1 ? "obiect" : "obiecte"}.`);
    } catch (e) { setEroare(mesajEroare(e)); setMesaj(""); }
  }

  if (lista === null) return <div className="gol">{tr("Se încarcă…")}</div>;

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}
      {mesaj && <div className="mesaj">{mesaj}</div>}

      <div className="panou">
        <div className="panou-antet">
          <h2>{tr("Obligațiile clientului")}</h2>
          <span className="numar">{lista.length}</span>
          {poateScrie && (
            <button className="buton-mic" onClick={() => setNoua(true)}>{tr("scrie o obligație")}</button>
          )}
        </div>
        <div className="panou-corp">
          {lista.length === 0 ? (
            <div className="gol">
              {tr("Nicio obligație. Ia una din lista de subiecte de mai jos sau scrie-o de la zero — în ambele cazuri, temeiul îl scrii tu.")}
            </div>
          ) : (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr>
                    <th>{tr("Obligație")}</th>
                    <th>{tr("Se aplică la")}</th>
                    <th>{tr("Frecvență")}</th>
                    <th>{tr("Temei")}</th>
                    {poateScrie && <th>{tr("Acțiuni")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {lista.map((o) => (
                    <tr key={o.id} className={o.temeiLegal ? "" : "rand-neconform"}>
                      <td>
                        {tr(o.denumire)}
                        <span className="cod-mic">{tr("v")}{o.versiune}</span>
                        <div className="rezultat-context">
                          {sursaAleasa(o) ? tr("sursă nealeasă încă")
                            : eticheta(SURSE_OBLIGATIE, o.sursaObligatiei)}
                          {o.consecintaNerespectare ? ` · ${o.consecintaNerespectare}` : ""}
                        </div>
                      </td>
                      <td>{eticheta(OBIECTE_OBLIGATIE, o.seAplicaLa)}</td>
                      <td>
                        {eticheta(FRECVENTE, o.frecventa)}
                        {o.tolerantaZile ? (
                          <div className="rezultat-context">{tr("toleranță")} {o.tolerantaZile} {tr("zile")}</div>
                        ) : null}
                      </td>
                      <td>
                        {o.temeiLegal ? (
                          <>
                            {o.temeiLegal}
                            <div className="rezultat-context">
                              {tr("asumat de")} {o.completatDe?.numeComplet ?? "—"}, {data(o.completatLa)}
                            </div>
                          </>
                        ) : (
                          <span className="eticheta eticheta-inactiv">{tr("temei necompletat")}</span>
                        )}
                      </td>
                      {poateScrie && (
                        <td className="celula-actiuni">
                          <button className="buton-mic" onClick={() => setEditata(o)}>{tr("editează")}</button>
                          <button className="buton-mic" onClick={() => setIstoric(o)}>{tr("istoric")}</button>
                          <button className="buton-mic" onClick={() => genereaza(o)}
                            disabled={!o.temeiLegal}
                            title={o.temeiLegal ? "" : tr("Scrie întâi temeiul: fără el, obligația nu produce scadențe.")}>
                            {tr("generează scadențe")}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="panou">
        <div className="panou-antet">
          <h2>{tr("Subiecte de acoperit")}</h2>
          <span className="numar">{sabloane.length}</span>
        </div>
        <div className="panou-corp">
          <p className="nota-cautare">
            {tr("Lista e o listă de SUBIECTE, nu de texte de lege: spune ce trebuie acoperit, nu ce scrie legea. Temeiul îl scrie firma, care și-l asumă — noi n-avem cum să dăm consultanță juridică, iar un articol greșit citat în dosarul unui control e mai rău decât unul lipsă.")}
          </p>
          <div className="tabel-derulabil">
            <table className="tabel">
              <thead>
                <tr>
                  <th>{tr("Subiect")}</th>
                  <th>{tr("Se aplică la")}</th>
                  <th>{tr("Sugerat")}</th>
                  {poateScrie && <th>{tr("Acțiuni")}</th>}
                </tr>
              </thead>
              <tbody>
                {sabloane.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {tr(s.denumire)}
                      <span className="cod-mic">{s.cod}</span>
                      {s.explicatie && <div className="rezultat-context">{s.explicatie}</div>}
                    </td>
                    <td>{eticheta(OBIECTE_OBLIGATIE, s.seAplicaLa)}</td>
                    <td>{s.frecventaSugerata ? eticheta(FRECVENTE, s.frecventaSugerata) : "—"}</td>
                    {poateScrie && (
                      <td className="celula-actiuni">
                        {luate.has(s.id) ? (
                          <span className="marcaj">{tr("deja luat")}</span>
                        ) : (
                          <button className="buton-mic" onClick={() => copiaza(s.id)}>ia-l</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {(editata || noua) && (
        <FormObligatie clientId={clientId} obligatie={editata}
          inchide={() => { setEditata(null); setNoua(false); }}
          gata={async () => { setEditata(null); setNoua(false); await reincarca(); }} />
      )}
      {istoric && <Istoric obligatie={istoric} inchide={() => setIstoric(null)} />}
    </>
  );
}

function FormObligatie({ clientId, obligatie, inchide, gata }) {
  const [f, setF] = useState({
    denumire: obligatie?.denumire ?? "",
    sursaObligatiei: obligatie?.sursaObligatiei ?? "Lege",
    temeiLegal: obligatie?.temeiLegal ?? "",
    frecventa: obligatie?.frecventa ?? "A",
    tolerantaZile: obligatie?.tolerantaZile ?? 0,
    seAplicaLa: obligatie?.seAplicaLa ?? "SistemInstalat",
    consecintaNerespectare: obligatie?.consecintaNerespectare ?? "",
    motiv: "",
  });
  const [eroare, setEroare] = useState("");
  const [salveaza, setSalveaza] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function trimite() {
    setSalveaza(true);
    try {
      const corp = {
        denumire: f.denumire,
        sursaObligatiei: f.sursaObligatiei,
        frecventa: f.frecventa,
        tolerantaZile: Number(f.tolerantaZile) || 0,
        seAplicaLa: f.seAplicaLa,
      };
      if (f.temeiLegal.trim()) corp.temeiLegal = f.temeiLegal.trim();
      if (f.consecintaNerespectare.trim()) corp.consecintaNerespectare = f.consecintaNerespectare.trim();
      if (obligatie) {
        if (f.motiv.trim()) corp.motiv = f.motiv.trim();
        await api.actualizeazaObligatie(obligatie.id, corp);
      } else {
        await api.creeazaObligatie({ ...corp, clientId });
      }
      await gata();
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setSalveaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{obligatie ? tr("Editează obligația") : tr("Scrie o obligație")}</h3>
        {/* Erorile apar ÎN modal: un banner în spatele lui nu se vede. */}
        {eroare && <div className="eroare">{eroare}</div>}

        <label className="camp">{tr("Denumire")}
          <input value={f.denumire} onChange={set("denumire")}
            placeholder={tr("ce anume trebuie ținut la zi")} />
        </label>

        <div className="formular-linie">
          <label className="camp">{tr("De unde vine")}
            <select value={f.sursaObligatiei} onChange={set("sursaObligatiei")}>
              {Object.entries(SURSE_OBLIGATIE).map(([v, e]) => (
                <option key={v} value={v}>{tr(e)}</option>
              ))}
            </select>
          </label>
          <label className="camp">{tr("Se aplică la")}
            <select value={f.seAplicaLa} onChange={set("seAplicaLa")}>
              {Object.entries(OBIECTE_OBLIGATIE).map(([v, e]) => (
                <option key={v} value={v}>{tr(e)}</option>
              ))}
            </select>
          </label>
          <label className="camp">{tr("Frecvență")}
            <select value={f.frecventa} onChange={set("frecventa")}>
              {Object.entries(FRECVENTE).map(([v, e]) => (
                <option key={v} value={v}>{tr(e)}</option>
              ))}
            </select>
          </label>
          <label className="camp">{tr("Toleranță (zile)")}
            <input type="number" min="0" max="365"
              value={f.tolerantaZile} onChange={set("tolerantaZile")} />
          </label>
        </div>

        <label className="camp-textarea">{tr("Temeiul")}
          <textarea rows={3} value={f.temeiLegal} onChange={set("temeiLegal")}
            placeholder={tr("ex. Legea 333/2003, art. ... — scris de firmă, asumat de firmă")} />
        </label>
        <p className="nota-cautare">
          {tr("Îl scrii tu și ți-l asumi: aplicația consemnează cine l-a scris și când, iar dosarul îi tipărește numele lângă obligație. Fără temei, obligația rămâne ciornă — nu produce scadențe și nu intră în dosar.")}
        </p>

        <label className="camp-textarea">{tr("Ce se întâmplă dacă nu se respectă")}
          <textarea rows={2} value={f.consecintaNerespectare}
            onChange={set("consecintaNerespectare")} />
        </label>

        {obligatie && (
          <label className="camp">{tr("De ce se schimbă")}
            <input value={f.motiv} onChange={set("motiv")}
              placeholder={tr("intră în istoricul obligației")} />
          </label>
        )}

        <div className="modal-actiuni">
          <button className="buton-discret" onClick={inchide}>{tr("renunță")}</button>
          <button className="buton-actiune" onClick={trimite}
            disabled={salveaza || !f.denumire.trim()}>
            {salveaza ? tr("se salvează…") : tr("salvează")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Istoric({ obligatie, inchide }) {
  const [versiuni, setVersiuni] = useState(null);
  useEffect(() => { api.versiuniObligatie(obligatie.id).then(setVersiuni).catch(() => setVersiuni([])); },
    [obligatie.id]);

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Istoricul obligației")}</h3>
        <p className="nota-cautare">
          {tr("Fiecare versiune rămâne, inclusiv cea în care obligația s-a născut fără temei. La un control, tocmai aia se întreabă: de când.")}
        </p>
        {versiuni === null ? (
          <div className="gol">{tr("Se încarcă…")}</div>
        ) : (
          <div className="tabel-derulabil">
            <table className="tabel">
              <thead>
                <tr><th>{tr("Versiune")}</th><th>{tr("Când")}</th><th>{tr("Cine")}</th><th>{tr("De ce")}</th><th>{tr("Temei")}</th></tr>
              </thead>
              <tbody>
                {versiuni.map((v) => (
                  <tr key={v.id}>
                    <td>{tr("v")}{v.versiune}</td>
                    <td>{dataOra(v.modificatLa)}</td>
                    <td>{v.modificatDe?.numeComplet ?? "—"}</td>
                    <td>{v.motiv}</td>
                    <td>{v.continut?.temeiLegal ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="modal-actiuni">
          <button className="buton-discret" onClick={inchide}>{tr("închide")}</button>
        </div>
      </div>
    </div>
  );
}

// ── Dosarele: documentul predat, congelat, cu amprentă ──────────────────────

function Dosare({ clientId, poateGenera }) {
  const [lista, setLista] = useState(null);
  const [eroare, setEroare] = useState("");
  const [formular, setFormular] = useState(false);
  const [deschis, setDeschis] = useState(null);

  const reincarca = useCallback(async () => {
    setLista(null);
    try { setLista(await api.dosareConformitate(clientId)); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); setLista([]); }
  }, [clientId]);

  useEffect(() => { reincarca(); }, [reincarca]);

  if (lista === null) return <div className="gol">{tr("Se încarcă…")}</div>;

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou">
        <div className="panou-antet">
          <h2>{tr("Dosare generate")}</h2>
          <span className="numar">{lista.length}</span>
          {poateGenera && (
            <button className="buton-mic" onClick={() => setFormular(true)}>{tr("generează un dosar")}</button>
          )}
        </div>
        <div className="panou-corp">
          <p className="nota-cautare">
            {tr("Un dosar e o fotografie a registrului la un moment dat, cu amprentă SHA-256: dacă cineva contestă mai târziu ce s-a predat, amprenta spune dacă documentul e cel dintâi. Regenerarea face un dosar NOU; cel vechi rămâne neatins.")}
          </p>
          {lista.length === 0 ? (
            <div className="gol">{tr("Niciun dosar generat pentru acest client.")}</div>
          ) : (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr>
                    <th>{tr("Număr")}</th><th>{tr("Perioadă")}</th><th>{tr("Pentru")}</th>
                    <th>{tr("Generat")}</th><th>{tr("Neprobate")}</th><th>{tr("Amprentă")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <button className="buton-discret" onClick={() => setDeschis(d.id)}>
                          {d.numar}
                        </button>
                      </td>
                      <td>{data(d.perioadaDeLa)} – {data(d.perioadaPanaLa)}</td>
                      <td>{eticheta(DESTINATII_DOSAR, d.destinatie)}</td>
                      <td>
                        {dataOra(d.generatLa)}
                        <div className="rezultat-context">{d.generatDe?.numeComplet ?? "—"}</div>
                      </td>
                      <td>
                        {d.elementeNeprobate?.length ? (
                          <span className="eticheta marcaj-atentie">
                            {d.elementeNeprobate.length}
                          </span>
                        ) : "—"}
                      </td>
                      <td><span className="cod-mic">{String(d.hashSHA256).slice(0, 12)}…</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {formular && (
        <FormDosar clientId={clientId} inchide={() => setFormular(false)}
          gata={async () => { setFormular(false); await reincarca(); }} />
      )}
      {deschis && <VezDosar id={deschis} inchide={() => setDeschis(null)} />}
    </>
  );
}

function FormDosar({ clientId, inchide, gata }) {
  const azi = new Date();
  const anTrecut = new Date(azi.getFullYear() - 1, azi.getMonth(), azi.getDate());
  const iso = (d) => d.toISOString().slice(0, 10);
  const [f, setF] = useState({
    perioadaDeLa: iso(anTrecut), perioadaPanaLa: iso(azi), destinatie: "Control",
  });
  const [eroare, setEroare] = useState("");
  const [lucreaza, setLucreaza] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function trimite() {
    setLucreaza(true);
    try {
      await api.genereazaDosar({
        clientId,
        perioadaDeLa: new Date(f.perioadaDeLa).toISOString(),
        perioadaPanaLa: new Date(f.perioadaPanaLa).toISOString(),
        destinatie: f.destinatie,
      });
      await gata();
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Generează un dosar")}</h3>
        {eroare && <div className="eroare">{eroare}</div>}
        <div className="formular-linie">
          <label className="camp">{tr("De la")}
            <input type="date" value={f.perioadaDeLa} onChange={set("perioadaDeLa")} />
          </label>
          <label className="camp">{tr("Până la")}
            <input type="date" value={f.perioadaPanaLa} onChange={set("perioadaPanaLa")} />
          </label>
        </div>
        <label className="camp">{tr("Pentru cine")}
          <select value={f.destinatie} onChange={set("destinatie")}>
            {Object.entries(DESTINATII_DOSAR).map(([v, e]) => (
              <option key={v} value={v}>{tr(e)}</option>
            ))}
          </select>
        </label>
        <div className="modal-actiuni">
          <button className="buton-discret" onClick={inchide}>{tr("renunță")}</button>
          <button className="buton-actiune" onClick={trimite} disabled={lucreaza}>
            {lucreaza ? tr("se generează…") : tr("generează")}
          </button>
        </div>
      </div>
    </div>
  );
}

function VezDosar({ id, inchide }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.dosarConformitate(id).then(setD).catch(() => setD(false)); }, [id]);

  if (d === null) return null;
  const c = d?.continut ?? {};

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Dosar")} {d?.numar}</h3>
        {d === false ? (
          <div className="eroare">{tr("Dosarul nu s-a putut încărca.")}</div>
        ) : (
          <>
            <p className="nota-cautare">
              {tr(c.prestator?.denumire)} {tr("pentru")} {tr(c.client?.denumire)} ·
              {" "}{data(c.perioada?.deLa)} – {data(c.perioada?.panaLa)} ·
              {" "}{tr("generat de")} {c.generat?.de}, {dataOra(c.generat?.la)}
            </p>
            <p className="nota-cautare">
              {tr("Amprentă SHA-256:")} <span className="cod-mic">{d.hashSHA256}</span>
            </p>

            {/* Un tabel cu antet și fără rânduri se citește ca o randare
                stricată, nu ca „nimic în perioadă". Golul se spune. */}
            {(c.obligatii ?? []).length === 0 ? (
              <p className="nota-cautare">
                {tr("Nicio obligație cu scadență în perioada aleasă. Fie perioada e greșită, fie obligațiile clientului n-au încă temei scris — atunci se văd mai jos, la elemente neprobate.")}
              </p>
            ) : (
              <div className="tabel-derulabil">
                <table className="tabel">
                  <thead>
                    <tr><th>{tr("Obligație")}</th><th>{tr("Obiect")}</th><th>{tr("Scadență")}</th><th>{tr("Stare")}</th><th>{tr("Asumat de")}</th></tr>
                  </thead>
                  <tbody>
                    {c.obligatii.map((o, i) => (
                      <tr key={i}>
                        <td>
                          {tr(o.denumire)}
                          <div className="rezultat-context">{o.temeiLegal ?? tr("temei necompletat")}</div>
                        </td>
                        <td>{o.obiect}</td>
                        <td>{data(o.scadenta)}</td>
                        <td>{eticheta(STARI_CONFORMITATE, o.status)}</td>
                        <td>
                          {o.asumatDe ?? "—"}
                          {o.asumatLa && <div className="rezultat-context">{data(o.asumatLa)}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Golurile se tipăresc, nu se ascund: un dosar care tace acolo
                unde nu știe e mai periculos decât unul care spune unde. */}
            <h3 style={{ marginTop: "1.4rem" }}>{tr("Elemente neprobate")}</h3>
            {(d.elementeNeprobate ?? []).length === 0 ? (
              <p className="nota-cautare">{tr("Nimic — fiecare obligație din perioadă are dovadă.")}</p>
            ) : (
              <div className="tabel-derulabil">
                <table className="tabel">
                  <thead><tr><th>{tr("Obligație")}</th><th>{tr("Obiect")}</th><th>{tr("De ce")}</th></tr></thead>
                  <tbody>
                    {d.elementeNeprobate.map((e, i) => (
                      <tr key={i}><td>{e.obligatie}</td><td>{e.obiect}</td><td>{e.motiv}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        <div className="modal-actiuni">
          <button className="buton-discret" onClick={inchide}>{tr("închide")}</button>
        </div>
      </div>
    </div>
  );
}
